/**
 * EdgeDerivationService
 *
 * E018-S009: Pure function service that derives required edges from block state.
 * NO SIDE EFFECTS - only computes what edges should exist.
 */

import { FILE_INDEX_MANAGED_BY } from '@streamlain/database-identity';

import type { IEdgeDerivationService } from '../core/interfaces/IEdgeDerivationService';
import type {
  IncomingAutomationInfo,
  TrashedLocalAutomationInfo,
} from '../core/interfaces/IGhostDerivationService';
import type {
  AutomationEdgeMetadata,
  EdgeDefinition,
  FileIndexEdgeMetadata,
  RegisteredAutomation,
  RegisteredDatabase,
  RegisteredElster,
  RegisteredFileSource,
  RegisteredGhost,
  RelationEdgeMetadata,
} from '../core/types';
import { generateEdgeDefinitionId } from '../core/types';

// =============================================================================
// Helper Types
// =============================================================================

interface Endpoint {
  elementId: string;
  blockId: string;
}

// =============================================================================
// Implementation
// =============================================================================

/**
 * Service that derives which edges are needed based on current block state.
 *
 * This is a PURE FUNCTION service - no side effects, fully testable.
 *
 * Key principle: Edges are DERIVED STATE from blocks.
 */
export class EdgeDerivationService implements IEdgeDerivationService {
  /**
   * Derives which edges are required based on current block state.
   */
  deriveRequiredEdges(
    databases: RegisteredDatabase[],
    automations: RegisteredAutomation[],
    ghosts: RegisteredGhost[],
    incomingAutomations?: IncomingAutomationInfo[],
    trashedLocalAutomations?: TrashedLocalAutomationInfo[],
    elsters?: RegisteredElster[],
    fileSources?: RegisteredFileSource[]
  ): EdgeDefinition[] {
    const edges: EdgeDefinition[] = [];

    // Build lookup maps for fast endpoint resolution
    const dbMap = new Map(databases.map(db => [db.blockId, db]));
    const ghostMap = new Map(ghosts.map(g => [g.targetId, g]));

    // Track created edges to avoid duplicates (for bidirectional relations)
    const createdEdgeKeys = new Set<string>();

    // PART 1: Automation Edges
    for (const auto of automations) {
      // Track which table IDs already have input edges (avoid duplicates)
      const inputEdgeTableIds = new Set<string>();

      // 1a. Lead input edge via legacy sourceTableId
      if (auto.sourceTableId) {
        const source = this.findEndpoint(auto.sourceTableId, dbMap, ghostMap);
        if (source) {
          const edge = this.createAutomationEdge(
            source,
            { elementId: auto.elementId, blockId: auto.blockId },
            auto.sourceTableId,
            auto.blockId,
            'input'
          );
          edges.push(edge);
          inputEdgeTableIds.add(auto.sourceTableId);
        }
      }

      // 1b. sl-m379: Input edges from inputTables (manual secondaries)
      for (const inputTable of auto.inputTables ?? []) {
        // Skip if already covered by sourceTableId (lead)
        if (inputEdgeTableIds.has(inputTable.tableId)) continue;

        const source = this.findEndpoint(inputTable.tableId, dbMap, ghostMap);
        if (source) {
          const edge = this.createAutomationEdge(
            source,
            { elementId: auto.elementId, blockId: auto.blockId },
            inputTable.tableId,
            auto.blockId,
            'input'
          );
          edges.push(edge);
          inputEdgeTableIds.add(inputTable.tableId);
        }
      }

      // 1c. Auto-detected related tables: lead's relation columns with cardinality='one'
      if (auto.sourceTableId) {
        const leadDb = dbMap.get(auto.sourceTableId);
        if (leadDb) {
          for (const rel of leadDb.relations) {
            if (
              rel.cardinality === 'one' &&
              !inputEdgeTableIds.has(rel.targetDatabaseId)
            ) {
              const relSource = this.findEndpoint(
                rel.targetDatabaseId,
                dbMap,
                ghostMap
              );
              if (relSource) {
                const edge = this.createAutomationEdge(
                  relSource,
                  { elementId: auto.elementId, blockId: auto.blockId },
                  rel.targetDatabaseId,
                  auto.blockId,
                  'input'
                );
                edges.push(edge);
                inputEdgeTableIds.add(rel.targetDatabaseId);
              }
            }
          }
        }
      }

      // 1c. Output edges for each table in outputTables
      for (const outputTable of auto.outputTables ?? []) {
        const target = this.findEndpoint(outputTable.tableId, dbMap, ghostMap);
        if (target) {
          const edge = this.createAutomationEdge(
            { elementId: auto.elementId, blockId: auto.blockId },
            target,
            outputTable.tableId,
            auto.blockId,
            'output'
          );
          edges.push(edge);
        }
      }
    }

    // PART 2: Automation Ghost Edges (incoming + trashed local)
    // E018-S014: Use incomingAutomations + trashedLocalAutomations to create ALL edges
    // - incomingAutomations: ghosts from OTHER docs that reference local databases
    // - trashedLocalAutomations: ghosts from deleted automations in SAME doc
    const allAutomationGhostSources = [
      ...(incomingAutomations ?? []),
      ...(trashedLocalAutomations ?? []),
    ];

    if (allAutomationGhostSources.length > 0) {
      // Build ghost lookup by automationId (targetId)
      const automationGhostMap = new Map<string, RegisteredGhost>();
      for (const ghost of ghosts) {
        if (ghost.ghostType === 'automation' && ghost.targetId) {
          automationGhostMap.set(ghost.targetId, ghost);
        }
      }

      // Create edges for ALL automation ghost connections (incoming + trashed local)
      for (const incoming of allAutomationGhostSources) {
        const ghost = automationGhostMap.get(incoming.automationId);
        if (!ghost) continue;

        const localDb = dbMap.get(incoming.localDatabaseId);
        if (!localDb) continue;

        // Direction determines edge direction:
        // - 'source' = DB is automation's INPUT → DB → Ghost (data flows to automation)
        // - 'target' = DB is automation's OUTPUT → Ghost → DB (data flows from automation)
        if (incoming.direction === 'source') {
          // DB is input: DB → Automation Ghost
          const edge = this.createAutomationEdge(
            { elementId: localDb.elementId, blockId: localDb.blockId },
            { elementId: ghost.elementId, blockId: ghost.blockId },
            localDb.blockId,
            ghost.targetId, // automationId
            'input'
          );
          edges.push(edge);
        } else {
          // DB is output: Automation Ghost → DB
          const edge = this.createAutomationEdge(
            { elementId: ghost.elementId, blockId: ghost.blockId },
            { elementId: localDb.elementId, blockId: localDb.blockId },
            localDb.blockId,
            ghost.targetId, // automationId
            'output'
          );
          edges.push(edge);
        }
      }
    } else {
      // Fallback: Use ghost.automationDirection (legacy behavior)
      for (const ghost of ghosts) {
        if (ghost.ghostType !== 'automation') continue;

        const localDb = dbMap.get(ghost.sourceBlockId);
        if (!localDb) continue;

        const direction = ghost.automationDirection;
        if (!direction) continue;

        if (direction === 'source') {
          const edge = this.createAutomationEdge(
            { elementId: localDb.elementId, blockId: localDb.blockId },
            { elementId: ghost.elementId, blockId: ghost.blockId },
            localDb.blockId,
            ghost.targetId,
            'input'
          );
          edges.push(edge);
        } else {
          const edge = this.createAutomationEdge(
            { elementId: ghost.elementId, blockId: ghost.blockId },
            { elementId: localDb.elementId, blockId: localDb.blockId },
            localDb.blockId,
            ghost.targetId,
            'output'
          );
          edges.push(edge);
        }
      }
    }

    // PART 3: ELSTER Edges (Database ↔ ELSTER)
    // ELSTER only has input edge now (history is in Prisma)
    for (const elster of elsters ?? []) {
      // 3a. Input edge: Database → ELSTER (via inputTableId)
      if (elster.inputTableId) {
        const source = this.findEndpoint(elster.inputTableId, dbMap, ghostMap);
        if (source) {
          const edge = this.createAutomationEdge(
            source,
            { elementId: elster.elementId, blockId: elster.blockId },
            elster.inputTableId,
            elster.blockId,
            'input'
          );
          edges.push(edge);
        }
      }

      // 3b. History is now in Prisma (sl-wlcm) — no output edge needed
    }

    // PART 4: Relation Edges
    for (const db of databases) {
      for (const rel of db.relations) {
        const targetEndpoint = this.findEndpoint(
          rel.targetDatabaseId,
          dbMap,
          ghostMap
        );
        if (!targetEndpoint) continue;

        // Create unique key for deduplication (sorted to handle bidirectional)
        const edgeKey = this.createRelationEdgeKey(
          db.blockId,
          rel.targetDatabaseId,
          rel.columnId
        );

        // Check if inverse edge already created
        const inverseKey = rel.inverseFieldId
          ? this.createRelationEdgeKey(
              rel.targetDatabaseId,
              db.blockId,
              rel.inverseFieldId
            )
          : null;

        if (createdEdgeKeys.has(edgeKey)) continue;
        if (inverseKey && createdEdgeKeys.has(inverseKey)) continue;

        const isSelfRelation = db.blockId === rel.targetDatabaseId;

        const edge = this.createRelationEdge(
          { elementId: db.elementId, blockId: db.blockId },
          targetEndpoint,
          db.blockId,
          rel.targetDatabaseId,
          rel.columnId,
          rel.cardinality,
          rel.inverseCardinality ?? 'many',
          !!rel.inverseFieldId,
          rel.inverseFieldId,
          isSelfRelation
        );
        edges.push(edge);
        createdEdgeKeys.add(edgeKey);
      }
    }

    // PART 5: File Index Edges
    // The doc-scoped File Index mirrors source image/attachment blocks. Edges
    // connect the database node to each source block on the same canvas.
    const fileIndexDatabases = databases.filter(
      db => db.managedBy === FILE_INDEX_MANAGED_BY && db.visible !== false
    );
    for (const fileIndexDb of fileIndexDatabases) {
      for (const fileSource of fileSources ?? []) {
        if (fileSource.blockId === fileIndexDb.blockId) continue;
        edges.push(this.createFileIndexEdge(fileIndexDb, fileSource));
      }
    }

    return edges;
  }

  /**
   * Finds an endpoint (database or ghost) by ID.
   */
  private findEndpoint(
    id: string,
    dbMap: Map<string, RegisteredDatabase>,
    ghostMap: Map<string, RegisteredGhost>
  ): Endpoint | null {
    // First check databases
    const db = dbMap.get(id);
    if (db) {
      return { elementId: db.elementId, blockId: db.blockId };
    }

    // Then check ghosts
    const ghost = ghostMap.get(id);
    if (ghost) {
      return { elementId: ghost.elementId, blockId: ghost.blockId };
    }

    return null;
  }

  /**
   * Creates an automation edge definition.
   */
  private createAutomationEdge(
    source: Endpoint,
    target: Endpoint,
    databaseId: string,
    automationId: string,
    direction: 'input' | 'output'
  ): EdgeDefinition {
    const metadata: AutomationEdgeMetadata = {
      kind: 'automation',
      direction,
      automationId,
      databaseId,
    };

    return {
      id: generateEdgeDefinitionId(
        'automation',
        source.elementId,
        target.elementId,
        direction
      ),
      sourceId: source.elementId,
      targetId: target.elementId,
      type: 'automation',
      metadata,
      // Automation data flow is horizontally oriented by contract:
      // database right → automation input left → automation output right →
      // database left. Never let auto-routing select top/bottom ports.
      sourceAnchor: 'right',
      targetAnchor: 'left',
    };
  }

  /**
   * Creates a relation edge definition.
   * Self-relations (isSelfLoop) use 'top' anchors so the rendering service
   * draws a counterclockwise loop above the database node.
   */
  private createRelationEdge(
    source: Endpoint,
    target: Endpoint,
    sourceDatabaseId: string,
    targetDatabaseId: string,
    relationColumnId: string,
    sourceCardinality: 'one' | 'many',
    targetCardinality: 'one' | 'many',
    isBidirectional: boolean,
    inverseColumnId?: string,
    isSelfLoop?: boolean
  ): EdgeDefinition {
    const metadata: RelationEdgeMetadata = {
      kind: 'relation',
      sourceDatabaseId,
      targetDatabaseId,
      relationColumnId,
      sourceCardinality,
      targetCardinality,
      isBidirectional,
      inverseColumnId,
    };

    return {
      id: generateEdgeDefinitionId(
        'relation',
        source.elementId,
        target.elementId,
        relationColumnId
      ),
      sourceId: source.elementId,
      targetId: target.elementId,
      type: 'relation',
      metadata,
      // Self-relations: anchor at top for visual loop above the database
      sourceAnchor: isSelfLoop ? 'top' : undefined,
      targetAnchor: isSelfLoop ? 'top' : undefined,
    };
  }

  private createFileIndexEdge(
    fileIndexDatabase: Endpoint,
    fileSource: Endpoint
  ): EdgeDefinition {
    const metadata: FileIndexEdgeMetadata = {
      kind: 'file-index',
      sourceDatabaseId: fileIndexDatabase.blockId,
      sourceBlockId: fileSource.blockId,
    };

    return {
      id: generateEdgeDefinitionId(
        'relation',
        fileIndexDatabase.elementId,
        fileSource.elementId,
        `file-index-${fileSource.blockId}`
      ),
      sourceId: fileIndexDatabase.elementId,
      targetId: fileSource.elementId,
      type: 'relation',
      metadata,
      sourceAnchor: 'auto',
      targetAnchor: 'auto',
    };
  }

  /**
   * Creates a unique key for relation edge deduplication.
   */
  private createRelationEdgeKey(
    sourceDbId: string,
    targetDbId: string,
    columnId: string
  ): string {
    // Sort IDs to ensure same key for both directions
    const ids = [sourceDbId, targetDbId].sort();
    return `rel-${ids[0]}-${ids[1]}-${columnId}`;
  }
}
